// Top-level page for a single adaptive-sampling campaign, mounted at
// /campaigns/:id by the router. Owns the tab selection, reads the campaign
// state via react-query, and subscribes to the WebSocket stream.

import { useCallback, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text } from '@radix-ui/themes';

import { useCampaignStateQuery, useCampaignStream } from '../api';
import { SurfacesTab } from '../surfaces';
import { ProvenanceTab } from '../provenance';
import { CampaignHeader } from './CampaignHeader';
import { TabSwitcher, type CampaignTabKey } from './TabSwitcher';
import { SamplerTab } from './SamplerTab';
import { ConvergenceTab } from './ConvergenceTab';
import { PointsTab } from './PointsTab';
import { ThreadLogo } from '../../../components/ThreadLogo';

const VALID_TABS: readonly CampaignTabKey[] = [
  'surfaces',
  'sampler',
  'convergence',
  'points',
  'provenance',
] as const;

const DEFAULT_TAB: CampaignTabKey = 'surfaces';

// Dark background to match the article theme. When W8 publishes a real
// `adaptiveSamplingTheme`, swap this constant for the import.
const CAMPAIGN_BACKGROUND = '#0a0a0a';

export interface CampaignViewProps {
  /** 'page' = full-screen route with Thread chrome (default).
   *  'panel' = embedded in SlidePanel, no chrome, campaign id is provided
   *           by the parent instead of the route params. */
  mode?: 'page' | 'panel';
  campaignIdOverride?: string;
}

export function CampaignView(props: CampaignViewProps = {}) {
  const { mode = 'page', campaignIdOverride } = props;
  const isPanel = mode === 'panel';
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const campaignId = campaignIdOverride ?? id ?? '';

  const activeTab: CampaignTabKey = useMemo(() => {
    const raw = searchParams.get('tab');
    if (raw && (VALID_TABS as readonly string[]).includes(raw)) {
      return raw as CampaignTabKey;
    }
    return DEFAULT_TAB;
  }, [searchParams]);

  const setActiveTab = useCallback(
    (next: CampaignTabKey) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === DEFAULT_TAB) nextParams.delete('tab');
          else nextParams.set('tab', next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const stateQuery = useCampaignStateQuery(campaignId);
  // Side-effect: WebSocket subscription that invalidates the state query.
  useCampaignStream(campaignId);

  // Mirror status into document.title for quick browser-tab scanning.
  useEffect(() => {
    if (stateQuery.data) {
      const s = stateQuery.data;
      document.title = `${s.config.name} — ${s.status}`;
    }
  }, [stateQuery.data]);

  return (
    <Flex
      direction="column"
      style={{
        height: isPanel ? '100%' : '100vh',
        background: isPanel ? 'transparent' : CAMPAIGN_BACKGROUND,
        color: 'var(--gray-12)',
      }}
    >
      {/* Thread chrome — keeps the standalone campaign page visibly part of
          Thread. Panel mode skips this since the Explore panel provides
          its own chrome. */}
      {!isPanel && (
        <Flex
          align="center"
          justify="between"
          px="4"
          py="2"
          style={{
            borderBottom: '1px solid var(--gray-a4)',
            background: '#05060a',
          }}
        >
          <Flex align="center" gap="3">
            <Link to="/" aria-label="Back to Thread" style={{ display: 'inline-flex' }}>
              <ThreadLogo height={18} />
            </Link>
            <Text size="1" color="gray">/</Text>
            <Text size="1" color="gray">Adaptive sampling campaign</Text>
          </Flex>
          <Link to="/" style={{ color: 'var(--accent-11)', fontSize: 12 }}>
            Projects ↗
          </Link>
        </Flex>
      )}

      {stateQuery.data ? (
        <CampaignHeader state={stateQuery.data} />
      ) : (
        <Box
          p="3"
          style={{
            borderBottom: '1px solid var(--gray-a5)',
            background: 'var(--color-panel-solid)',
          }}
        >
          <Text size="2" color="gray">
            {stateQuery.isLoading
              ? 'Loading campaign...'
              : stateQuery.isError
                ? `Error: ${(stateQuery.error as Error)?.message}`
                : 'Campaign not found'}
          </Text>
        </Box>
      )}

      <TabSwitcher value={activeTab} onChange={setActiveTab} />

      <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {activeTab === 'surfaces' && <SurfacesTab campaignId={campaignId} />}
        {activeTab === 'sampler' && <SamplerTab campaignId={campaignId} />}
        {activeTab === 'convergence' && (
          <ConvergenceTab campaignId={campaignId} />
        )}
        {activeTab === 'points' && <PointsTab campaignId={campaignId} />}
        {activeTab === 'provenance' && (
          <ProvenanceTab campaignId={campaignId} />
        )}
      </Box>
    </Flex>
  );
}

export default CampaignView;
